import PageMeta from "../components/PageMeta";
import { cityLabel } from "../utils/placeLabels";
import uiI18n from "../i18n";
import ListingSort, { normalizeSort } from '../components/ListingSort';
import React, { useState, useEffect, useRef } from "react";
import SaveSearchPrompt from "../components/SaveSearchPrompt";
import ListBiens from "../components/ListBiens";
import Spinner from '../components/Spinner';
import SearchForm from './SearchForm';
import citiesAndDelegations from '../data/citiesAndDelegations.js';
import { useTranslation } from "react-i18next";
import { getBiens } from '../Services/biensService';
import { useSearchParams } from "react-router-dom";

export default function CategoryByVille() {
  const [searchParams, setSearchParams] = useSearchParams();
  const city = searchParams.get('city') || '';

  const [offerListings, setOfferListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);
  // Offered after each search, saved only if the visitor accepts (SaveSearchPrompt).
  const [searchOffer, setSearchOffer] = useState(null);
  const [selectedDelegation, setSelectedDelegation] = useState('');
  const [category, setCategory] = useState('');
  const sort = normalizeSort(searchParams.get('sort'), false, category);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [libelle, setLibelle] = useState('');
  const [rooms, setRooms] = useState([]);
  const requestVersion = useRef(0);
  const moreRequest = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    setOfferListings([]);

    refreshListOffers();
    return () => { requestVersion.current++; };
  }, [city, sort]);

  const refreshListOffers = async () => {
    const version = ++requestVersion.current;
    moreRequest.current = null;
    setLoading(true);
    setError(null);
    try {
      const filters = {
        sort,
        city,
        delegation: selectedDelegation,
        category,
        minPrice,
        maxPrice,
        libelle,
        rooms
      }; 
       
      const offersData = await getBiens(0, filters);
      if (version !== requestVersion.current) return;
      setSearchOffer({ ...filters, kind: 'bien' });
      setOfferListings(offersData);
      setLoading(false);
      setHasMore(offersData.length === 20);
      setPage(0);
    } catch (error) {
      if (version === requestVersion.current) setError(error.message);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const onFetchMoreListings = async () => {
    if (loading || moreRequest.current !== null || !hasMore) return;
    const version = requestVersion.current;
    moreRequest.current = version;
    try {
      const newPage = page + 1;
      const filters = { city, delegation: selectedDelegation, category, minPrice, maxPrice, libelle, rooms, sort };

      const additionalData = await getBiens(newPage, filters);
      if (version !== requestVersion.current) return;
      setOfferListings(prevListings => [...prevListings, ...additionalData]);
      setHasMore(additionalData.length === 20);
      setPage(newPage);
    } catch (error) {
      console.error('Une erreur s\'est produite :', error);
    } finally {
      if (version === requestVersion.current) moreRequest.current = null;
    }
  };

  return (
    <div className='max-w-6xl mx-auto px-3'>
      <PageMeta titleKey={city ? 'seo.searchCity' : 'seo.categoryAll'} values={{ city: cityLabel(city) }} path="/categorybyville" />
      <h1 className='text-3xl text-center mt-6 font-bold mb-6'>
        {t('ads')} {city && `à ${city}`}
      </h1>

      <SearchForm 
        citiesAndDelegations={citiesAndDelegations} 
        selectedCity={city}
        setSelectedDelegation={setSelectedDelegation}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        category={category}
        setCategory={setCategory}
        libelle={libelle}
        setLibelle={setLibelle}
        rooms={rooms}
        setRooms={setRooms}
        handleSearch={refreshListOffers}
      />
 
      <ListingSort value={sort} category={category} onChange={value => {
        const next = new URLSearchParams(searchParams); next.delete('page');
        if (value === 'recent') next.delete('sort'); else next.set('sort', value);
        setSearchParams(next);
      }} />
      <SaveSearchPrompt search={searchOffer} />
      {loading ? (
        <Spinner />
      ) : offerListings.length > 0 ? (
        <>
          <p className="text-[#457b9d] mb-2 font-semibold">
            {offerListings.length} {uiI18n.t("ui.offers_found_in")} {city}
          </p>
          <main>
            <ul className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {offerListings.map((bien) => (
                <ListBiens 
                  key={bien.ID} 
                  listing={bien} />
              ))}
            </ul>
          </main>
          {hasMore && (
            <div className="flex justify-center items-center">
              <button 
                onClick={onFetchMoreListings}
                className="bg-white px-3 py-1.5 text-gray-700 border border-gray-300 mb-6 mt-6 hover:border-state-600 rounded transition duration-150 ease-in-out"
              >
                {t('see_plus')}
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-[#0b2738] mb-2 font-semibold">{t('No_ads')}</p>
      )}
    </div>
  );
}
